import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { useCheck } from "../contexts/CheckContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

const TEASER_TEXTS = {
    low: {
        title: "Eher geringe Relevanz",
        text: "Nach Ihrer Eingabe spricht vieles dafür, dass ein Rückgriff auf Kinder in der Regel eher nicht im Vordergrund steht. Eine vollständige Einordnung Ihrer Situation erhalten Sie nach dem kostenlosen Login.",
    },
    medium: {
        title: "Prüfung empfehlenswert",
        text: "In Ihrer Konstellation kann eine nähere Prüfung sinnvoll sein, insbesondere wenn Heimkosten, Vermögen und Einkommen nicht ausreichen oder unklar sind.",
    },
    high: {
        title: "Vertiefte Prüfung besonders relevant",
        text: "Nach Ihrer Eingabe könnte Ihre Situation vertieft prüfungsrelevant sein. Gerade oberhalb der 100.000-€-Grenze lohnt sich eine strukturierte individuelle Analyse.",
    },
};

export default function TeaserResultPage() {
    const { teaser, answers } = useCheck();
    const { user } = useAuth();
    const navigate = useNavigate();

    if (!teaser) return <Navigate to="/check" replace />;
    const t = TEASER_TEXTS[teaser.risk_level] || TEASER_TEXTS.medium;

    const goUnlock = () => {
        if (user) navigate("/ergebnis");
        else navigate("/register", { state: { intent: "unlock-result" } });
    };

    return (
        <div className="min-h-[80vh] bg-brand-50/30 py-10">
            <div className="container max-w-3xl">
                <div className="rounded-3xl border border-ink-300/30 bg-white p-6 shadow-softer sm:p-10">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        <Sparkles className="h-4 w-4" /> Ihre Ersteinschätzung
                    </div>
                    <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        {t.title}
                    </h1>
                    <p className="mt-4 text-ink-500">{t.text}</p>

                    <div className="mt-6 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl bg-brand-50 p-4">
                            <div className="text-xs uppercase tracking-wider text-brand-700">Risikolevel</div>
                            <div className="mt-1 font-display text-lg font-semibold">
                                {teaser.risk_level === "high" && "Hoch"}
                                {teaser.risk_level === "medium" && "Mittel"}
                                {teaser.risk_level === "low" && "Niedrig"}
                            </div>
                        </div>
                        <div className="rounded-2xl bg-brand-50 p-4">
                            <div className="text-xs uppercase tracking-wider text-brand-700">Paket-Empfehlung</div>
                            <div className="mt-1 font-display text-lg font-semibold">
                                {teaser.package_recommendation}
                            </div>
                        </div>
                        <div className="rounded-2xl bg-brand-50 p-4">
                            <div className="text-xs uppercase tracking-wider text-brand-700">Region</div>
                            <div className="mt-1 font-display text-lg font-semibold capitalize">
                                {(answers.region || "baden-wuerttemberg").replace(/-/g, " ")}
                            </div>
                        </div>
                    </div>

                    {/* Locked preview */}
                    <div className="relative mt-8 overflow-hidden rounded-2xl border border-ink-300/30">
                        <div className="space-y-2 p-6">
                            <div className="h-5 w-2/3 rounded bg-ink-300/30" />
                            <div className="h-4 w-full rounded bg-ink-300/20" />
                            <div className="h-4 w-5/6 rounded bg-ink-300/20" />
                            <div className="mt-4 h-24 rounded bg-ink-300/15" />
                            <div className="h-4 w-3/4 rounded bg-ink-300/20" />
                        </div>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/0 via-white/70 to-white" />
                        <div className="absolute inset-0 flex flex-col items-center justify-end p-6">
                            <div className="flex flex-col items-center gap-3 text-center">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-white shadow-soft">
                                    <Lock className="h-5 w-5" />
                                </span>
                                <div className="font-display text-lg font-semibold">Vollständige Einschätzung freischalten</div>
                                <div className="max-w-sm text-sm text-ink-500">
                                    Kostenlos registrieren oder anmelden, um konkrete Empfehlungen, Kostenstruktur
                                    und passendes Beratungspaket zu sehen.
                                </div>
                                <Button
                                    onClick={goUnlock}
                                    className="mt-2 h-12 rounded-full bg-brand-500 px-6 hover:bg-brand-600"
                                    data-testid="teaser-unlock"
                                >
                                    Vollergebnis freischalten <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <Link to="/login" className="text-xs text-brand-700 underline" data-testid="teaser-login-link">
                                    Bereits Konto? Jetzt anmelden
                                </Link>
                            </div>
                        </div>
                    </div>

                    <ul className="mt-6 grid gap-2 text-sm text-ink-700 sm:grid-cols-2">
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-500" />
                            Kostenstruktur mit Beispielregion
                        </li>
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-500" />
                            Einordnung der 100.000-€-Frage
                        </li>
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-500" />
                            Nächste sinnvolle Schritte
                        </li>
                        <li className="flex items-start gap-2">
                            <ShieldCheck className="mt-0.5 h-4 w-4 text-brand-500" />
                            Kostenlos, vertraulich, DSGVO-konform
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
